import React from 'react';
import { PathwordStoreType } from '../PathwordStore';
import { Store } from 'pullstate';

type GridCellProps = {
  store: Store<PathwordStoreType>;
  row: number;
  col: number;
  isVowel: boolean;
  startPath: (row: number, col: number) => void;
  continuePath: (row: number, col: number) => void;
};

const GridCell = React.memo(({
  store, row, col, isVowel, startPath, continuePath,
}: GridCellProps) => {
  const { path, cluetext, pathHead, lines, highlights, successAnimationGrid, revealed, solved, solutionPath, solutionLines, hintPositions, enabledHints, hintLetters }
    = store.useState();

  // Check if this cell has an enabled hint letter
  const posKey = `${row}_${col}`;
  const hintIndex = hintPositions[posKey];
  const hintLetterPosition = hintIndex !== undefined ? hintLetters.indexOf(hintIndex) : -1;
  const isHintEnabled = hintLetterPosition !== -1 && enabledHints.includes(hintLetterPosition);
  const hintLetterForCell = isHintEnabled && !revealed && !solved ? cluetext[hintIndex] : null;

  const hasPath = path[row][col] > 0;
  const showHintPreview = hintLetterForCell && !hasPath;

  // Check if this hint cell is highlighted (next letter matches)
  const highlighted = highlights[row][col] > 0;
  const isHintHighlighted = showHintPreview && highlighted;

  const classes = `matrixCell`
    + `${isVowel ? ' vowelCell' : ''}`
    + ` ${solved || revealed ? solutionLines[solutionPath[row][col]] : (lines[path[row][col]] || '')}`
    + `${highlighted && !showHintPreview ? ' highlighted' : ''}`
    + `${solved || (successAnimationGrid[row][col] > 0) ? ' solved' : ''}`
    + `${showHintPreview ? ' hintPreview' : ''}`
    + `${isHintHighlighted ? ' hintHighlighted' : ''}`;

  const highlightContent = pathHead ? cluetext[path[pathHead[0]][pathHead[1]]] : '';
  const pathContent = solved || revealed ? cluetext[solutionPath[row][col] - 1] : cluetext[path[row][col] - 1];

  // Check if this cell contains a used hint letter
  const pathIndex = path[row][col] - 1; // Convert to 0-indexed
  const isUsedHintCell = pathIndex >= 0 && isHintEnabled;

  return (
    <div
      className={classes}
      onMouseDown={() => startPath(row, col)}
      onMouseEnter={() => continuePath(row, col)}
      onTouchStart={() => startPath(row, col)}
      onContextMenu={(e) => { e.preventDefault(); }}
      data-rowindex={row}
      data-colindex={col}
    >
      {pathContent &&
        <div className={`circledLetter ${isUsedHintCell && !solved ? 'usedHint' : ''}`}>
          {pathContent}
        </div>}
      {!pathContent && showHintPreview &&
        <div className={`hintPreviewLetter ${isHintHighlighted ? 'unfaded' : ''}`}>
          {hintLetterForCell}
        </div>}
      {highlighted && !showHintPreview &&
        <div>
          {highlightContent}
        </div>}
    </div>
  );
});

export default GridCell;